import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { HeaderComponent } from '@shared/layout/header/header.component';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ComponentsModule, SnackBarService } from '@mastechnicaegroup/carelinelive-ui';
import { createAddressFormGroup } from '@lib/api/models/address';
import { createPersonDetailsFormGroup } from '@lib/api/models/person';
import { Router } from '@angular/router';
import { UserService } from '@lib/user/user.service';
import { SendableRequest } from '@mastechnicaegroup/carelinelive-cdk/requests';
import { createUserFormGroup, StoreUserRequest, UserSingleResponse } from '@lib/api/models/user';
import { FormValidationService } from '@shared/services/form-validation.service';
import { ErrorPanelComponent } from '@lib/errors/error-panel/error-panel.component';
import { AuthenticationService } from '@lib/auth/authentication.service';
import { catchError, debounceTime, distinctUntilChanged, filter, of, startWith, switchMap, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReferenceService } from '@shared/services/reference.service';

@Component({
    selector: 'app-users-add-page',
    imports: [
        HeaderComponent,
        FormsModule,
        ReactiveFormsModule,
        ComponentsModule,
        ErrorPanelComponent
    ],
    templateUrl: './users-add-page.component.html',
    standalone: true,
    styleUrl: './users-add-page.component.scss'
})
export class UsersAddPageComponent implements OnInit {

    protected readonly addressFormGroup = createAddressFormGroup();
    private readonly _userService = inject(UserService);
    private readonly _authService = inject(AuthenticationService);
    private readonly _referenceService = inject(ReferenceService);
    protected readonly userDetailsFormGroup = createUserFormGroup();
    protected readonly personDetailsFormGroup = createPersonDetailsFormGroup();
    private readonly _snackBar = inject(SnackBarService);
    private readonly _router = inject(Router);
    private readonly _formValidation = inject(FormValidationService);
    private readonly _destroyRef = inject(DestroyRef);

    request = new SendableRequest<UserSingleResponse, StoreUserRequest>({
        request: payload => this._userService.createUser(payload!),

        onSend: () => {
            this.userDetailsFormGroup.disable();
            this.personDetailsFormGroup.disable();
        },

        onSuccess: response => {
            this._snackBar.open({
                colour: 'success',
                icon: 'check',
                title: 'User added successfully.'
            });

            if (response.data.email) {
                this._authService.sendInvite({ email: response.data.email.toLowerCase() }).pipe(
                    take(1)
                ).subscribe({
                    next: () => {
                        this._snackBar.open({
                            colour: 'success',
                            icon: 'check',
                            title: 'Invitation sent to the user.'
                        });
                    },
                    error: () => {
                        this._snackBar.open({
                            colour: 'warning',
                            icon: 'warning',
                            title: 'User created but invitation could not be sent.'
                        });
                    }
                });
            }

            this._router.navigate(['/users', response.data.id])
                .then(success => {
                    if (!success) {
                        this._snackBar.open({
                            colour: 'error',
                            icon: 'error',
                            title: 'Failed to navigate to the user.'
                        });
                        return;
                    }
                });
        },

        onError: () => {
            this.userDetailsFormGroup.enable();
            this.personDetailsFormGroup.enable();
        }
    });

    ngOnInit(): void {
        const control = this.userDetailsFormGroup.controls['reference'];
        let skipNextReferenceCheck = false;

        control.valueChanges.pipe(
            startWith(control.value),
            filter(() => {
                if (!skipNextReferenceCheck) {
                    return true;
                }
                skipNextReferenceCheck = false;
                return false;
            }),
            debounceTime(300),
            distinctUntilChanged(),
            switchMap(value => {
                const normalisedReference = (value ?? '').trim();
                if (control.value !== normalisedReference) {
                    control.setValue(normalisedReference, { emitEvent: false });
                }
                return this._referenceService.checkReferenceUnique(normalisedReference, 'user').pipe(
                    catchError(() => of(null))
                );
            }),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe((response) => {
            if (!response) {
                control.updateValueAndValidity({ emitEvent: false });
                const errors = { ...(control.errors ?? {}) };
                delete errors['notUnique'];
                delete errors['alternatives'];
                control.setErrors(Object.keys(errors).length ? errors : null, { emitEvent: false });
                return;
            }

            if (response.data.is_valid && control.pristine && control.value !== response.data.reference) {
                skipNextReferenceCheck = true;
                control.setValue(response.data.reference, { emitEvent: true });
            }

            control.updateValueAndValidity({ emitEvent: false });
            const errors = { ...(control.errors ?? {}) };

            if (response.data.is_valid) {
                delete errors['notUnique'];
                delete errors['alternatives'];
            } else {
                errors['notUnique'] = true;
                errors['alternatives'] = response.data.alternatives ?? [];
            }

            control.setErrors(Object.keys(errors).length ? errors : null, { emitEvent: false });
        });
    }

    add(): void {
        if (this.request.loading()) {
            return;
        }

        if (!this.personDetailsFormGroup.valid || !this.userDetailsFormGroup.valid) {
            this._formValidation.markFormGroupTouched(this.personDetailsFormGroup);
            this._formValidation.markFormGroupTouched(this.userDetailsFormGroup);

            this._formValidation.showFormErrors(this.personDetailsFormGroup, true);
            this._formValidation.showFormErrors(this.userDetailsFormGroup, true);

            return;
        }

        const givenName = this.personDetailsFormGroup.value.given_name ?? '';
        const familyName = this.personDetailsFormGroup.value.family_name ?? '';
        const initials = (givenName.charAt(0) || '') + (familyName.charAt(0) || '');

        const userData: StoreUserRequest = {
            email: (this.userDetailsFormGroup.value.email ?? '').toLowerCase(),
            reference: this.userDetailsFormGroup.value.reference ?? '',
            send_email_messages: this.userDetailsFormGroup.value.send_email_messages ?? false,
            send_internal_messages: this.userDetailsFormGroup.value.send_internal_messages ?? false,
            person: {
                title: this.personDetailsFormGroup.value.title ?? '',
                given_name: givenName,
                middle_names: this.personDetailsFormGroup.value.middle_names ?? '',
                family_name: familyName,
                initials: initials,
                date_of_birth: this.personDetailsFormGroup.value.date_of_birth ?? '',
                preferred_name: this.personDetailsFormGroup.value.preferred_name ?? '',
                pronouns: this.personDetailsFormGroup.value.pronouns ?? '',
                birth_sex: this.personDetailsFormGroup.value.birth_sex ?? '',
                additional_comments: this.personDetailsFormGroup.value.additional_comments ?? ''
            }
        };

        this.request.send(userData);
    }

    cancel(): void {
        this._router.navigate(['/users'])
            .then(success => {
                if (!success) {
                    this._snackBar.open({
                        colour: 'error',
                        icon: 'error',
                        title: 'Failed to navigate to the user index page.'
                    });
                    return;
                }
            });
    }

    getInvalidControls(formGroup: FormGroup): string[] {
        const invalidControls: string[] = [];
        Object.keys(formGroup.controls).forEach(key => {
            const control = formGroup.get(key);
            if (control && control.invalid) {
                invalidControls.push(key);
            }
        });
        return invalidControls;
    }
}
